import { useMemo } from 'preact/hooks';
import { useLocale } from './LocaleProvider';
import type { MessageCatalog } from './types';

/**
 * Translation hook. Returns a `t(key, vars?)` function that resolves a
 * dot-path key against the active locale's catalog, falling back to the
 * fallback locale's catalog (typically EN), and finally to the key itself
 * if nothing matches.
 *
 * Optional `{name}` interpolation handles simple substitution; complex
 * pluralization is out of scope (handled per-call via `t` chains where
 * needed).
 *
 * Missing keys return the key string as a visible breadcrumb - that
 * makes "I forgot to add this translation" obvious in the page rather
 * than rendering an empty string.
 *
 * Usage:
 *   const t = useTranslation();
 *   t('nav.catalog')                          // "Browse the catalog"
 *   t('welcome.message', { name: 'Friend' })  // "Hello, {name}!" -> "Hello, Friend!"
 */
export function useTranslation() {
    const { messages, fallbackMessages } = useLocale();

    function t(key: string, vars?: Record<string, string | number>): string {
        let value = lookup(messages, key);
        if (value === undefined) value = lookup(fallbackMessages, key);
        if (value === undefined) return key;
        if (!vars) return value;
        return value.replace(/\{(\w+)\}/g, (_, k) => {
            const v = vars[k];
            return v !== undefined ? String(v) : `{${k}}`;
        });
    }

    return t;
}

/**
 * Like {@link useTranslation} but for an array-of-strings leaf (for example the
 * rotating hero lines under `landing.heroQuotes`). Resolves the dot-path
 * against the ACTIVE locale only and returns `[]` when the key is missing or is
 * not an array of strings. The result is memoised on the catalog + key so
 * callers get a stable reference across renders (safe in effect deps).
 *
 * Unlike {@link useTranslation}, this deliberately does NOT backfill from the
 * fallback locale: a rotating/structured set pulled from another language would
 * render as mixed-language content (English lines under an otherwise-translated
 * page). A locale that has not authored the set just gets an empty list, and
 * the caller falls back to its own translated single value instead.
 */
export function useTranslationList(key: string): string[] {
    const { messages } = useLocale();
    return useMemo(() => lookupList(messages, key) ?? [], [messages, key]);
}

function lookup(catalog: MessageCatalog, key: string): string | undefined {
    const parts = key.split('.');
    let cursor: unknown = catalog;
    for (const part of parts) {
        if (cursor === null || cursor === undefined) return undefined;
        if (typeof cursor !== 'object' || Array.isArray(cursor)) return undefined;
        cursor = (cursor as MessageCatalog)[part];
    }
    return typeof cursor === 'string' ? cursor : undefined;
}

function lookupList(catalog: MessageCatalog, key: string): string[] | undefined {
    const parts = key.split('.');
    let cursor: unknown = catalog;
    for (const part of parts) {
        if (cursor === null || cursor === undefined) return undefined;
        if (typeof cursor !== 'object' || Array.isArray(cursor)) return undefined;
        cursor = (cursor as MessageCatalog)[part];
    }
    return Array.isArray(cursor) && cursor.every((item) => typeof item === 'string')
        ? (cursor as string[])
        : undefined;
}

/**
 * Parse a translated string with `[[em]]…[[/em]]` markers indicating
 * accent-coloured highlighted spans (typically inside display headings).
 * Word order changes across languages, so the marker travels with the
 * translation - the renderer doesn't need to know where the highlight
 * falls. Returns an array of {text, highlighted} segments.
 */
export interface RichTextSegment {
    text: string;
    highlighted: boolean;
}

export function parseRichText(source: string): RichTextSegment[] {
    const segments: RichTextSegment[] = [];
    const re = /\[\[em\]\](.*?)\[\[\/em\]\]/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(source)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ text: source.slice(lastIndex, match.index), highlighted: false });
        }
        segments.push({ text: match[1], highlighted: true });
        lastIndex = re.lastIndex;
    }
    if (lastIndex < source.length) {
        segments.push({ text: source.slice(lastIndex), highlighted: false });
    }
    return segments;
}
